/**
 * Batch 18-J18 — Provider Unresolved Events Admin Panel
 *
 * Operator triage surface for provider evidence events that could
 * not fully resolve to OptiBay artifact and/or technician/user
 * records. Renders inside `TrainingCatalogAdmin` under the
 * `Provider Unresolved Events` tab. Backed by the J3 read RPCs
 * (providers / unmatched events / optional mapping context) + the
 * J7 reprocess mutation RPC.
 *
 * Scope:
 *   - provider selector via `list_training_providers_for_admin` (J3)
 *   - per-provider unresolved events list via
 *     `list_training_provider_unmatched_events_for_admin` (J3)
 *   - optional mapping context via
 *     `list_training_provider_artifact_mappings_for_admin` (J3) and
 *     `list_training_provider_user_mappings_for_admin` (J3) — used
 *     only to detect "mapping now available" classification
 *   - per-row reprocess via
 *     `reprocess_training_provider_evidence_event` (J7)
 *
 * Out of scope (deferred to later J-lane batches):
 *   - bulk reprocess
 *   - audit history UI
 *   - raw event payload viewer
 *   - provider registry CRUD
 *   - artifact/user mapping create/edit from this row
 *   - manager verification writes
 *   - provider evidence direct writes
 *   - completion/attestation/certification status creation
 *
 * Trust boundaries:
 *   - Reprocess is routing retry. It does NOT verify completion,
 *     create attestation, create evidence, or create manager
 *     verification.
 *   - The panel never reads provider tables directly; J3/J7 RPCs
 *     are the only seams.
 *   - Raw provider event payload, payload hash, signature status,
 *     idempotency key, HMAC secret material are never displayed.
 *   - Raw full external email from event payloads is never
 *     displayed; only `masked_external_user_email` is rendered.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  Loader2,
  Search,
  RefreshCw,
  Link2,
  User as UserIcon,
} from 'lucide-react';

// ─── Narrow local RPC casts ─────────────────────────────────────────

export interface TrainingProviderAdminRow {
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_key: string;
  display_name: string;
  provider_type: string;
  auth_type: string;
  timestamp_tolerance_seconds: number;
  is_active: boolean;
  secret_configured: boolean;
  public_key_configured: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
  event_count: number | string;
  has_unmatched_events: boolean;
}

export interface TrainingProviderUnresolvedEventAdminRow {
  event_id: string;
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_event_id: string;
  external_course_id: string | null;
  external_user_id: string | null;
  masked_external_user_email: string | null;
  event_type: string;
  event_status: string;
  processing_status: string;
  reconciliation_status: string | null;
  matched_artifact_id: string | null;
  matched_technician_profile_id: string | null;
  matched_user_id: string | null;
  received_at: string;
  attested_at: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface TrainingProviderArtifactMappingAdminRow {
  mapping_id: string;
  provider_id: string;
  external_course_id: string;
  training_artifact_id: string;
  is_active: boolean;
}

export interface TrainingProviderUserMappingAdminRow {
  mapping_id: string;
  provider_id: string;
  external_user_id: string | null;
  masked_external_user_email: string | null;
  external_user_email_verified: boolean;
  technician_profile_id: string | null;
  user_id: string | null;
  is_active: boolean;
}

interface ListProvidersRpc {
  (
    fn: 'list_training_providers_for_admin',
    args: {
      p_org_id: string;
      p_shop_id: string | null;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListUnmatchedEventsRpc {
  (
    fn: 'list_training_provider_unmatched_events_for_admin',
    args: {
      p_provider_id: string;
      p_status_filter: string[] | null;
      p_limit: number;
      p_offset: number;
    },
  ): Promise<{
    data: TrainingProviderUnresolvedEventAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListArtifactMappingsRpc {
  (
    fn: 'list_training_provider_artifact_mappings_for_admin',
    args: {
      p_provider_id: string;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderArtifactMappingAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListUserMappingsRpc {
  (
    fn: 'list_training_provider_user_mappings_for_admin',
    args: {
      p_provider_id: string;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderUserMappingAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ReprocessEventRpc {
  (
    fn: 'reprocess_training_provider_evidence_event',
    args: {
      p_event_id: string;
      p_actor_user_id: string;
      p_reason: string | null;
    },
  ): Promise<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
}

interface ProviderUnresolvedEventsAdminPanelProps {
  orgId: string | null | undefined;
  shopId: string | null | undefined;
  currentUserId: string | null | undefined;
}

function safeRpcError(error: { message: string; code?: string } | null): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') {
    return 'You do not have permission to perform that action.';
  }
  const msg = (error.message ?? '').trim();
  return msg.length === 0 ? 'Action failed.' : msg;
}

// ─── Row classification (pure helper) ──────────────────────────────

export type UnresolvedEventClassification =
  | 'missing_artifact_mapping'
  | 'missing_user_mapping'
  | 'missing_artifact_and_user_mapping'
  | 'mapping_now_available'
  | 'processing_failed'
  | 'pending_manager_review'
  | 'already_resolved';

export interface UnresolvedEventClassifyContext {
  artifactMappingCourseIds: Set<string>;
  userMappingExternalUserIds: Set<string>;
  userMappingMaskedEmails: Set<string>;
}

export function classifyUnresolvedEvent(
  row: TrainingProviderUnresolvedEventAdminRow,
  ctx: UnresolvedEventClassifyContext,
): UnresolvedEventClassification {
  if (row.reconciliation_status === 'pending_manager_review') {
    return 'pending_manager_review';
  }
  const hasArtifact = row.matched_artifact_id != null;
  const hasUser =
    row.matched_technician_profile_id != null || row.matched_user_id != null;
  if (hasArtifact && hasUser) {
    return 'already_resolved';
  }
  const failed =
    !!(row.error_code && row.error_code.trim().length > 0) ||
    row.processing_status === 'failed' ||
    row.processing_status === 'error';
  if (failed) {
    return 'processing_failed';
  }
  const artifactGap = !hasArtifact;
  const userGap = !hasUser;
  // "Mapping now available" — a mapping has been created for the
  // missing side(s) AFTER the event was received.
  const artifactNowMapped =
    !artifactGap ||
    (row.external_course_id != null &&
      ctx.artifactMappingCourseIds.has(row.external_course_id));
  const userNowMapped =
    !userGap ||
    (row.external_user_id != null &&
      ctx.userMappingExternalUserIds.has(row.external_user_id)) ||
    (row.masked_external_user_email != null &&
      ctx.userMappingMaskedEmails.has(row.masked_external_user_email));
  if ((artifactGap || userGap) && artifactNowMapped && userNowMapped) {
    return 'mapping_now_available';
  }
  if (artifactGap && userGap) return 'missing_artifact_and_user_mapping';
  if (artifactGap) return 'missing_artifact_mapping';
  if (userGap) return 'missing_user_mapping';
  return 'already_resolved';
}

function classificationLabel(c: UnresolvedEventClassification): string {
  switch (c) {
    case 'missing_artifact_mapping':
      return 'Missing artifact mapping';
    case 'missing_user_mapping':
      return 'Missing user mapping';
    case 'missing_artifact_and_user_mapping':
      return 'Missing artifact and user mapping';
    case 'mapping_now_available':
      return 'Mapping now available — Ready to reprocess';
    case 'processing_failed':
      return 'Processing failed';
    case 'pending_manager_review':
      return 'Pending manager review';
    case 'already_resolved':
      return 'Already resolved / no action';
  }
}

// ═══════════════════════════════════════════════════════════════════
// Top-level panel
// ═══════════════════════════════════════════════════════════════════

export function ProviderUnresolvedEventsAdminPanel({
  orgId,
  shopId,
  currentUserId,
}: ProviderUnresolvedEventsAdminPanelProps) {
  const queryClient = useQueryClient();

  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<
    | 'all'
    | 'missing_artifact_mapping'
    | 'missing_user_mapping'
    | 'missing_artifact_and_user_mapping'
    | 'mapping_now_available'
    | 'processing_failed'
    | 'pending_manager_review'
  >('all');
  const [externalCourseSearch, setExternalCourseSearch] = useState('');
  const [externalUserSearch, setExternalUserSearch] = useState('');
  const pageSize = 100;
  const page = 0;
  const statusFilter: string[] | null = null;

  const enabled = Boolean(orgId);

  // ── Provider list ────────────────────────────────────────────────
  const providersQuery = useQuery({
    queryKey: [
      'training-providers-admin',
      orgId ?? null,
      shopId ?? null,
      includeInactiveProviders,
    ],
    enabled,
    queryFn: async (): Promise<TrainingProviderAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListProvidersRpc;
      const { data, error } = await rpc('list_training_providers_for_admin', {
        p_org_id: orgId as string,
        p_shop_id: shopId ?? null,
        p_include_inactive: includeInactiveProviders,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const selectedProvider = useMemo<TrainingProviderAdminRow | null>(() => {
    if (!selectedProviderId) return null;
    return (
      providersQuery.data?.find((p) => p.provider_id === selectedProviderId) ??
      null
    );
  }, [providersQuery.data, selectedProviderId]);

  // ── Unresolved events list ───────────────────────────────────────
  const eventsQuery = useQuery({
    queryKey: [
      'training-provider-unresolved-events-admin',
      selectedProviderId,
      statusFilter,
      page,
    ],
    enabled: Boolean(selectedProviderId),
    queryFn: async (): Promise<TrainingProviderUnresolvedEventAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListUnmatchedEventsRpc;
      const { data, error } = await rpc(
        'list_training_provider_unmatched_events_for_admin',
        {
          p_provider_id: selectedProviderId as string,
          p_status_filter: statusFilter,
          p_limit: pageSize,
          p_offset: page * pageSize,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // ── Mapping context (artifact + user) ───────────────────────────
  const mappingContextQuery = useQuery({
    queryKey: [
      'training-provider-unresolved-event-mapping-context',
      selectedProviderId,
    ],
    enabled: Boolean(selectedProviderId),
    queryFn: async () => {
      const artifactRpc = supabase.rpc.bind(supabase) as unknown as ListArtifactMappingsRpc;
      const userRpc = supabase.rpc.bind(supabase) as unknown as ListUserMappingsRpc;
      const [artifactRes, userRes] = await Promise.all([
        artifactRpc('list_training_provider_artifact_mappings_for_admin', {
          p_provider_id: selectedProviderId as string,
          p_include_inactive: false,
        }),
        userRpc('list_training_provider_user_mappings_for_admin', {
          p_provider_id: selectedProviderId as string,
          p_include_inactive: false,
        }),
      ]);
      if (artifactRes.error) throw new Error(safeRpcError(artifactRes.error));
      if (userRes.error) throw new Error(safeRpcError(userRes.error));
      return {
        artifactMappings: artifactRes.data ?? [],
        userMappings: userRes.data ?? [],
      };
    },
  });

  const classifyCtx = useMemo<UnresolvedEventClassifyContext>(() => {
    const artifactIds = new Set<string>();
    const userIds = new Set<string>();
    const maskedEmails = new Set<string>();
    if (mappingContextQuery.data) {
      for (const m of mappingContextQuery.data.artifactMappings) {
        if (m.is_active) artifactIds.add(m.external_course_id);
      }
      for (const m of mappingContextQuery.data.userMappings) {
        if (!m.is_active) continue;
        if (m.external_user_id) userIds.add(m.external_user_id);
        if (m.masked_external_user_email)
          maskedEmails.add(m.masked_external_user_email);
      }
    }
    return {
      artifactMappingCourseIds: artifactIds,
      userMappingExternalUserIds: userIds,
      userMappingMaskedEmails: maskedEmails,
    };
  }, [mappingContextQuery.data]);

  const refetchEvents = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'training-provider-unresolved-events-admin',
        selectedProviderId,
        statusFilter,
        page,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'training-providers-admin',
        orgId ?? null,
        shopId ?? null,
        includeInactiveProviders,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'training-provider-unresolved-event-mapping-context',
        selectedProviderId,
      ],
    });
  };

  // ── Reprocess dialog ─────────────────────────────────────────────
  const [reprocessTarget, setReprocessTarget] =
    useState<TrainingProviderUnresolvedEventAdminRow | null>(null);
  const [reprocessReason, setReprocessReason] = useState('');

  useEffect(() => {
    if (reprocessTarget === null) {
      setReprocessReason('');
    }
  }, [reprocessTarget]);

  const reprocessMutation = useMutation({
    mutationFn: async () => {
      if (!reprocessTarget) throw new Error('No event selected.');
      if (!currentUserId) throw new Error('Sign in required.');
      if (reprocessReason.trim().length === 0) {
        throw new Error('Reason is required.');
      }
      const rpc = supabase.rpc.bind(supabase) as unknown as ReprocessEventRpc;
      const { error } = await rpc(
        'reprocess_training_provider_evidence_event',
        {
          p_event_id: reprocessTarget.event_id,
          p_actor_user_id: currentUserId,
          p_reason: reprocessReason.trim() || null,
        },
      );
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Reprocess requested.');
      refetchEvents();
      setReprocessTarget(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Derived rows + filters ──────────────────────────────────────
  const classifiedRows = useMemo(() => {
    return (eventsQuery.data ?? []).map((r) => ({
      row: r,
      classification: classifyUnresolvedEvent(r, classifyCtx),
    }));
  }, [eventsQuery.data, classifyCtx]);

  const visibleRows = useMemo(() => {
    const ec = externalCourseSearch.trim().toLowerCase();
    const eu = externalUserSearch.trim().toLowerCase();
    return classifiedRows.filter(({ row, classification }) => {
      if (reasonFilter !== 'all' && classification !== reasonFilter) {
        return false;
      }
      if (
        ec &&
        !(row.external_course_id ?? '').toLowerCase().includes(ec)
      ) {
        return false;
      }
      if (
        eu &&
        !(row.external_user_id ?? '').toLowerCase().includes(eu)
      ) {
        return false;
      }
      return true;
    });
  }, [classifiedRows, reasonFilter, externalCourseSearch, externalUserSearch]);

  const providerOptions = providersQuery.data ?? [];

  // ── Render ───────────────────────────────────────────────────────

  if (!enabled) {
    return (
      <Card>
        <CardContent
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="provider-unresolved-events-admin-panel"
        >
          You do not have access to provider unresolved events admin.
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="provider-unresolved-events-admin-panel"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Provider Unresolved Events</CardTitle>
          <CardDescription>
            Triage provider evidence events that could not fully resolve to
            OptiBay artifact and/or technician/user records. Reprocess is a
            routing retry — it does not verify training completion, create
            provider attestation, or create manager verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label
                htmlFor="provider-unresolved-events-provider-select"
                className="text-xs"
              >
                Provider
              </Label>
              <select
                id="provider-unresolved-events-provider-select"
                data-testid="provider-unresolved-events-provider-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedProviderId ?? ''}
                onChange={(e) =>
                  setSelectedProviderId(e.target.value || null)
                }
              >
                <option value="">Select a provider…</option>
                {providerOptions.map((p) => (
                  <option
                    key={p.provider_id}
                    value={p.provider_id}
                    data-testid={`provider-unresolved-events-provider-option-${p.provider_id}`}
                  >
                    {p.display_name} — {p.provider_key} ({p.auth_type})
                    {!p.is_active ? ' [inactive]' : ''}
                    {p.has_unmatched_events ? ' [unmatched events]' : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                data-testid="provider-unresolved-events-include-inactive-providers"
                checked={includeInactiveProviders}
                onCheckedChange={(v) =>
                  setIncludeInactiveProviders(Boolean(v))
                }
              />
              Include inactive providers
            </label>
          </div>

          {providersQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading providers…
            </div>
          )}
          {providersQuery.error && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="provider-unresolved-events-provider-error"
            >
              {/permission|42501/i.test(
                (providersQuery.error as Error).message,
              )
                ? 'You do not have access to provider unresolved events.'
                : 'Unable to load providers right now.'}
            </div>
          )}
          {!providersQuery.isLoading &&
            !providersQuery.error &&
            providerOptions.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No providers configured yet. Use the Training Providers tab to
                seed a provider first.
              </div>
            )}
        </CardContent>
      </Card>

      {!selectedProviderId ? (
        <Card>
          <CardContent
            className="py-12 text-center text-sm text-muted-foreground"
            data-testid="provider-unresolved-events-no-provider-selected"
          >
            Select a provider to triage unresolved events.
          </CardContent>
        </Card>
      ) : (
        <>
          {selectedProvider && !selectedProvider.is_active && (
            <div
              className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
              data-testid="provider-unresolved-events-inactive-provider-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This provider is inactive. Triage can be reviewed, but provider
                callbacks may not be accepted until the provider is
                reactivated.
              </span>
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Unresolved events</CardTitle>
              <CardDescription>
                Operational triage. Provider event received timestamp shown per
                row; raw payload, payload hash, signature status, and
                idempotency key are never displayed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  data-testid="provider-unresolved-events-reason-filter"
                  className="h-10 rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={reasonFilter}
                  onChange={(e) =>
                    setReasonFilter(
                      e.target.value as typeof reasonFilter,
                    )
                  }
                >
                  <option value="all">All unresolved reasons</option>
                  <option value="missing_artifact_mapping">
                    Missing artifact mapping
                  </option>
                  <option value="missing_user_mapping">
                    Missing user mapping
                  </option>
                  <option value="missing_artifact_and_user_mapping">
                    Missing artifact and user mapping
                  </option>
                  <option value="mapping_now_available">
                    Mapping now available
                  </option>
                  <option value="processing_failed">Processing failed</option>
                  <option value="pending_manager_review">
                    Pending manager review
                  </option>
                </select>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-unresolved-events-search-external-course"
                    placeholder="Search external course ID…"
                    value={externalCourseSearch}
                    onChange={(e) => setExternalCourseSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-unresolved-events-search-external-user"
                    placeholder="Search external user ID…"
                    value={externalUserSearch}
                    onChange={(e) => setExternalUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {eventsQuery.isLoading && (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading unresolved events…
                </div>
              )}

              {eventsQuery.error && (
                <div
                  className="py-8 text-center text-sm text-muted-foreground"
                  data-testid="provider-unresolved-events-list-error"
                >
                  {/permission|42501/i.test(
                    (eventsQuery.error as Error).message,
                  )
                    ? 'You do not have access to provider unresolved events.'
                    : 'Unable to load unresolved events right now.'}
                </div>
              )}

              {!eventsQuery.isLoading &&
                !eventsQuery.error &&
                classifiedRows.length === 0 && (
                  <div
                    className="py-8 text-center text-sm text-muted-foreground"
                    data-testid="provider-unresolved-events-healthy-empty"
                  >
                    No unresolved events for this provider.
                  </div>
                )}

              {visibleRows.length > 0 && (
                <ul
                  data-testid="provider-unresolved-events-list"
                  className="space-y-2"
                >
                  {visibleRows.map(({ row, classification }) => (
                    <li
                      key={row.event_id}
                      data-testid="provider-unresolved-events-row"
                      className="rounded-lg border bg-card p-3 flex flex-wrap items-start justify-between gap-3"
                    >
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={
                              classification === 'mapping_now_available'
                                ? 'default'
                                : classification === 'already_resolved'
                                ? 'secondary'
                                : classification === 'processing_failed'
                                ? 'destructive'
                                : 'outline'
                            }
                            data-testid="provider-unresolved-events-status"
                            className="text-xs"
                          >
                            {classificationLabel(classification)}
                          </Badge>
                          <code
                            data-testid="provider-unresolved-events-course"
                            className="font-mono text-xs"
                            title="External course ID"
                          >
                            {row.external_course_id ?? '—'}
                          </code>
                          <code
                            data-testid="provider-unresolved-events-user"
                            className="font-mono text-xs text-muted-foreground"
                            title="External provider user ID"
                          >
                            {row.external_user_id ?? '—'}
                          </code>
                          <span className="sr-only">Artifact match</span>
                          {row.matched_artifact_id == null && (
                            <Badge
                              variant="outline"
                              data-testid="provider-unresolved-events-artifact-gap"
                              className="text-xs"
                            >
                              Artifact gap
                            </Badge>
                          )}
                          <span className="sr-only">User match</span>
                          {row.matched_technician_profile_id == null &&
                            row.matched_user_id == null && (
                              <Badge
                                variant="outline"
                                data-testid="provider-unresolved-events-user-gap"
                                className="text-xs"
                              >
                                User gap
                              </Badge>
                            )}
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                          <div className="flex gap-2">
                            <dt>Provider event received:</dt>
                            <dd data-testid="provider-unresolved-events-received-at">
                              {row.received_at}
                            </dd>
                          </div>
                          <div className="flex gap-2">
                            <dt>Event status:</dt>
                            <dd>{row.event_status}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt>Processing status:</dt>
                            <dd>{row.processing_status}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt>Reconciliation status:</dt>
                            <dd>{row.reconciliation_status ?? '—'}</dd>
                          </div>
                          {row.error_code && (
                            <div className="flex gap-2">
                              <dt>Error code:</dt>
                              <dd
                                data-testid="provider-unresolved-events-error-code"
                                className="font-mono"
                              >
                                {row.error_code}
                              </dd>
                            </div>
                          )}
                          {row.error_message && (
                            <div className="flex gap-2 col-span-2">
                              <dt>Safe error message:</dt>
                              <dd
                                data-testid="provider-unresolved-events-error-message"
                                className="text-xs"
                              >
                                {row.error_message}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>

                      <div
                        className="flex flex-col items-end gap-2 shrink-0"
                        data-testid="provider-unresolved-events-action-panel"
                      >
                        <span className="sr-only">Actions</span>
                        {(classification === 'missing_artifact_mapping' ||
                          classification ===
                            'missing_artifact_and_user_mapping') && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid="provider-unresolved-events-open-artifact-mapping"
                            onClick={() => {
                              if (row.external_course_id) {
                                try {
                                  void navigator.clipboard?.writeText(
                                    row.external_course_id,
                                  );
                                  toast.success(
                                    'External course ID copied — paste into Provider Artifact Mapping.',
                                  );
                                } catch {
                                  /* no-op — clipboard not available */
                                }
                              }
                            }}
                          >
                            <Link2 className="h-4 w-4 mr-1" /> Open artifact mapping
                          </Button>
                        )}
                        {(classification === 'missing_user_mapping' ||
                          classification ===
                            'missing_artifact_and_user_mapping') && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid="provider-unresolved-events-open-user-mapping"
                            onClick={() => {
                              if (row.external_user_id) {
                                try {
                                  void navigator.clipboard?.writeText(
                                    row.external_user_id,
                                  );
                                  toast.success(
                                    'External user ID copied — paste into Provider User Mapping.',
                                  );
                                } catch {
                                  /* no-op — clipboard not available */
                                }
                              }
                            }}
                          >
                            <UserIcon className="h-4 w-4 mr-1" /> Open user mapping
                          </Button>
                        )}
                        {(classification === 'mapping_now_available' ||
                          classification === 'processing_failed') && (
                          <Button
                            size="sm"
                            data-testid="provider-unresolved-events-reprocess-action"
                            onClick={() => setReprocessTarget(row)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" /> Reprocess event
                          </Button>
                        )}
                        {classification === 'pending_manager_review' && (
                          <p className="text-xs text-muted-foreground max-w-[240px] text-right">
                            Provider evidence routing is separate from manager
                            verification.
                          </p>
                        )}
                        {classification === 'already_resolved' && (
                          <span className="text-xs text-muted-foreground">
                            No action required
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Reprocess dialog */}
      <Dialog
        open={reprocessTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReprocessTarget(null);
        }}
      >
        <DialogContent data-testid="provider-unresolved-events-reprocess-dialog">
          {reprocessTarget && (
            <>
              <DialogHeader>
                <DialogTitle>Reprocess provider event</DialogTitle>
                <DialogDescription>
                  Retry routing for this provider evidence event. Reprocess is
                  not training completion, not provider attestation, and not
                  manager verification.
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="font-medium">
                    {selectedProvider?.display_name ?? '—'}
                  </span>
                  {selectedProvider?.provider_key && (
                    <code className="font-mono text-muted-foreground">
                      {selectedProvider.provider_key}
                    </code>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">External course ID:</span>
                  <code className="font-mono">
                    {reprocessTarget.external_course_id ?? '—'}
                  </code>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">External user ID:</span>
                  <code className="font-mono">
                    {reprocessTarget.external_user_id ?? '—'}
                  </code>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">
                    Current missing match reason:
                  </span>
                  <span>
                    {classificationLabel(
                      classifyUnresolvedEvent(reprocessTarget, classifyCtx),
                    )}
                  </span>
                </div>
                <p className="text-muted-foreground">
                  Expected result if mapping now exists: the event resolves to
                  the bound OptiBay artifact and/or technician/user.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider-unresolved-events-reprocess-reason">
                  Reason <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="provider-unresolved-events-reprocess-reason"
                  data-testid="provider-unresolved-events-reprocess-reason"
                  placeholder="Operational note (visible in the admin audit log)"
                  value={reprocessReason}
                  onChange={(e) => setReprocessReason(e.target.value)}
                />
              </div>

              <DialogFooter>
                <Button
                  data-testid="provider-unresolved-events-reprocess-confirm"
                  disabled={
                    reprocessMutation.isPending ||
                    reprocessReason.trim().length === 0
                  }
                  onClick={() => reprocessMutation.mutate()}
                >
                  {reprocessMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Reprocess
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
